import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AssetProperty } from '../../../../model/asset-property';
import { Tenant } from '../../../../model/tenant';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PropertyType } from '../../../../model/property-type';
import { TenantsService } from '../../../../services/tenants.service';
import { ActionButton } from '../../../../model/ui/action-button';
import { AssetsService } from '../../../../services/assets.service';
import { translate } from '@ngneat/transloco';
import { Asset } from '../../../../model/asset';
import { noSpecialCharacters } from '../../../../shared/custom-validators';

@Component({
  selector: 'app-assets-form-properties',
  templateUrl: './assets-form-properties.component.html',
  styleUrls: ['./assets-form-properties.component.scss']
})
export class AssetsFormPropertiesComponent implements OnInit {

  @Input() mode: 'create' | 'edit';

  @Input() assetProperties: AssetProperty[];
  @Output() assetPropertiesChange = new EventEmitter<AssetProperty[]>();
  @Output() onPreviousStep = new EventEmitter<void>();
  @Output() onNextStep = new EventEmitter<void>();
  @Output() onCancel = new EventEmitter<void>();

  @Input() tenant: Tenant;
  tenantIsEmpty: boolean;

  assetPropertiesForm: FormGroup;
  rowsForm: FormArray;

  propertyTypes: PropertyType[];
  clonedProperties: { [s: string]: AssetProperty; } = {};

  displayCopyFromAsset: boolean;
  copyFromAssetActions: ActionButton[];

  copyAssetProperties: AssetProperty[];

  constructor(private tenantsService: TenantsService,
              private assetsService: AssetsService,
              private formBuilder: FormBuilder) { }

  ngOnInit(): void {
    this.buildCopyFromAssetActions();
    if (!this.tenant) {
      this.tenantIsEmpty = true;
    } else {
      this.loadAndCreateForm();
    }
  }

  private loadAndCreateForm(): void {
    this.tenantsService.getTenantPropertyTypes(this.tenant.uuid).toPromise().then(response => {
      this.propertyTypes = response.propertyTypes
        .sort((a, b) => a.name > b.name ? 1 : -1);
      this.createForm(this.assetProperties);
      this.copyAssetProperties = this.assetProperties;
    });
  }

  private createForm(assetProperties: AssetProperty[]): void {
    this.rowsForm = this.formBuilder.array(
      assetProperties.map(property => this.formGroupFromAssetProperty(property))
    );
    this.assetPropertiesForm = this.formBuilder.group({
      assetProperties: this.rowsForm
    });
  }

  addProperty(): void {
    const newProperty = this.formGroupFromAssetProperty(new AssetProperty());
    this.rowsForm.push(newProperty);
  }

  onRowDelete(rowIndex: number): void {
    this.rowsForm.removeAt(rowIndex);
  }

  deleteAllRows(): void {
    while (this.rowsForm.length > 0) {
      this.rowsForm.removeAt(0);
    }
  }

  previousStep(): void {
    const properties = this.assetPropertiesForm.get('assetProperties').value;
    this.onPreviousStep.emit();
    this.assetPropertiesChange.emit(properties);
  }

  nextStep(): void {
    if (this.assetPropertiesForm.valid) {
      const properties = this.assetPropertiesForm.get('assetProperties').value;
      properties.forEach(property => {
        if (property.uuid == null) {
          property.uuid = this.copyAssetProperties.find(copyProperty => copyProperty.name === property.name)?.uuid;
        }
      });
      this.onNextStep.emit();
      this.assetPropertiesChange.emit(properties);
    }
  }

  private formGroupFromAssetProperty(property: AssetProperty): FormGroup {
    return this.formBuilder.group({
      name: [ property.name, [
        Validators.maxLength(255),
        Validators.required,
        noSpecialCharacters
      ]],
      value: [ property.value ],
      propertyType: [ property.propertyType, [Validators.required] ],
      uuid: [ property.uuid ]
    });
  }

  cancelAction(): void {
    this.onCancel.emit();
  }

  private buildCopyFromAssetActions(): void {
    this.copyFromAssetActions = [
      {
        label: translate('misc.copyProperties'),
        icon: 'pi pi-copy',
        action: (asset: Asset) => this.copyProperties(asset)
      }, {
        label: translate('misc.appendProperties'),
        icon: 'pi pi-plus',
        action: (asset: Asset) => this.copyProperties(asset, true)
      }
    ];
  }

  copyProperties(asset: Asset, append: boolean = false): void {
    this.assetsService.getAssetProperties(asset.uuid).subscribe(response => {
      if (!append) {
        this.deleteAllRows();
      }
      response.assetProperties.forEach((property: AssetProperty) => {
        property.uuid = this.copyAssetProperties.find(copyProperty => copyProperty.name === property.name)?.uuid;
        this.rowsForm.push(this.formGroupFromAssetProperty(property));
      });
      this.displayCopyFromAsset = false;
    });
  }

}
